/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';

import compressQueryFetch from './compressQueryFetch';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function initGraphQlClients(config) {
    let headers = {};

    // Add Store header to select Magento store
    if (config.storeView && config.storeView.trim() !== '') {
        headers['Store'] = config.storeView;
    }

    // Add Preview-Version header to display staged catalog data
    if (config.previewDate) {
        headers['Preview-Version'] = config.previewDate;
    }

    // Add additional headers from config
    if ('headers' in config) {
        for (let header of config.headers) {
            const parts = header.split(':');
            if (parts.length !== 2) {
                continue;
            }
            const [key, value] = parts;
            headers[key.trim()] = value.trim();
        }
    }

    // Use GET requests only if configured and if no staged data is requested
    const useGETForQueries = config.method && config.method === 'GET' && !config.previewDate ? true : false;

    // Initialize GraphQL client for each configuration
    const enableUIDSupport =
        config.enableUIDSupport && (config.enableUIDSupport === 'true' || config.enableUIDSupport === true);
    config.gqlClient = new ApolloClient({
        cache: new InMemoryCache({
            typePolicies: {
                CategoryTree: {
                    keyFields: enableUIDSupport ? ['uid'] : ['id']
                },
                ConfigurableProduct: {
                    keyFields: ['sku']
                },
                SimpleProduct: {
                    keyFields: ['sku']
                }
            }
        }),
        link: new HttpLink({
            uri: config.instanceUrl,
            headers,
            useGETForQueries,
            fetch: compressQueryFetch
        })
    });

    /**
     * Custom method to set / unset headers and HTTP method.
     */
    config.gqlClient.setLinkWithHeadersAndMethod = function (headerArray, method) {
        // Get values from existing link
        const { uri, headers } = this.link.options;

        // Set new headers
        let newHeaders = { ...headers };
        for (let [key, value] of headerArray) {
            if (value) {
                newHeaders[key] = value;
            } else {
                delete newHeaders[key];
            }
        }

        // Create and set new link
        this.setLink(
            new HttpLink({
                uri,
                headers: newHeaders,
                useGETForQueries: method === 'GET' && !config.previewDate ? true : false,
                fetch: compressQueryFetch
            })
        );
    }.bind(config.gqlClient);

    return config;
}

export function addIdentifiers(config) {
    if (config.identifier) {
        return {
            ...config,
            __typename: 'Configuration'
        };
    }

    let identifier = 'invalid';

    // Handle default config
    if (config.path && (config.path.startsWith('/libs') || config.path.startsWith('/apps'))) {
        identifier = 'cif-default';
    } else if (config.path) {
        // Looks for /conf/*/settings/cloudconfigs/commerce paths
        let match = config.path.match(/\/conf\/(.*)\/settings\/cloudconfigs\/commerce/);
        if (match && match.length > 1) {
            match = match[1];
        } else {
            // Use complete path if no direct match can be found
            match = config.path;
        }

        identifier = match
            .split('/') // Separate individual parts of path
            .map(s => s.replace(/[\W]+/g, '')) // Remove non alphanumeric letters
            .join('-'); // Concat
    }

    return {
        ...config,
        identifier,
        __typename: 'Configuration'
    };
}
